import { cloneDeep, isEqual } from 'lodash';
import { EStringOp } from '@src/shared/types';

export const isEmptyOrNotEmptyOp = (data): boolean => {
  return data === EStringOp.IS_EMPTY || data === EStringOp.IS_NOT_EMPTY;
};

export const getEmptyRuleGroupInAPIForm = (): CriticalEventRule => ({
  key: 'all_event_name',
  op: EStringOp.EQUALS,
  value: [] as string[],
  mapping_rules: [
    [
      {
        tag_key: {
          op: EStringOp.EQUALS,
          value: [],
        },
        tag_value: {
          op: EStringOp.EQUALS,
          value: [],
        },
      },
    ],
  ],
});

export const trimMappedEventRules = (rules: CriticalEventRule[][] = []) => {
  // filter out events that have empty event name and empty rules
  const clonedRules = cloneDeep(rules);
  const eventRules = clonedRules?.[0] ?? [];

  const res = eventRules.filter((eventRule) => {
    // Trim event name values
    if (eventRule?.value) {
      eventRule.value = eventRule.value.map((v) => v?.trim()).filter((v) => v);
    }

    const hasEventName = eventRule?.key?.length > 0 && eventRule?.value?.length > 0;

    // filter out mapping rules that have empty tag key and empty tag value
    const validMappingRules =
      eventRule?.mapping_rules?.filter((ruleItem) => {
        ruleItem = ruleItem.filter((tagRuleItem) => {
          // Trim tag_key and tag_value values
          if (tagRuleItem.tag_key?.value) {
            tagRuleItem.tag_key.value = tagRuleItem.tag_key.value
              .map((v) => v?.trim())
              .filter((v) => v);
          }
          if (tagRuleItem.tag_value?.value) {
            tagRuleItem.tag_value.value = tagRuleItem.tag_value.value
              .map((v) => v?.trim())
              .filter((v) => v);
          }
          if (Array.isArray(tagRuleItem.tag_value?.label)) {
            tagRuleItem.tag_value.label = tagRuleItem.tag_value.label
              .map((v) => v?.trim())
              .filter((v) => v);
          }

          return (
            tagRuleItem.tag_key?.value?.length > 0 ||
            tagRuleItem.tag_value?.value?.length > 0 ||
            isEmptyOrNotEmptyOp(tagRuleItem.tag_value?.op)
          );
        });
        return ruleItem.length > 0;
      }) ?? [];

    eventRule.mapping_rules = [...validMappingRules];

    // return true if the event name is not empty or there are valid mapping rules
    return hasEventName || validMappingRules.length > 0;
  });

  if (res.length === 0) {
    res.push(getEmptyRuleGroupInAPIForm());
  }
  return [res];
};

// Universal function: traverses all rules
const checkAllRules = (rules: any[], callback: (rule: any) => boolean): boolean => {
  return rules.every((andRules) =>
    andRules.every((orRule) =>
      orRule.mapping_rules.every((andMappingRules) =>
        andMappingRules.every((orMappingRule) => callback(orMappingRule)),
      ),
    ),
  );
};

type ValidateResult = {
  key?: string[];
  result?: string;
  status: boolean;
  mappedEvent?: any;
  fields: string[];
  // for example, { 0: [0, 1], 1: [2] } means that the first rule has errors in the first and second items, and the second rule has an error in the third item.
  errorIndex: Record<number, number[]>;
};

export const VALIDATE_SECURE_INPUT_MESSAGE =
  'The SQL statement is not permitted in this field. Please try a different one.';
export const MATCH_PATTERN_ERROR_MESSAGE = `Include more matching characters. The value cannot be only the asterisk (* ) wildcard.`;
export function isSecureInput(input: string): boolean {
  const regex =
    /\b(insert\s+into|delete\s+from|drop\s+table|drop\s+database|update\s+.+\s+set|alter\s+table|truncate\s+table|\bcreate\s+table\b|\bor\s+1\s*=\s*1\b)/i;
  return !regex.test(input);
}
export const isLegalString = (value: string): boolean => {
  if (value === '') return true;

  const regex =
    /^[\u4e00-\u9fa5\w\s|()[\]\-/.{}'"#$%^&!;?=@:*,+~áàâãéêíóôõúüñçÁÀÂÃÉÊÍÓÔÕÚÜÑÇ\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]+$/u;
  return regex.test(value);
};
export const isOnlyStars = (str: string = '') => {
  return /^\*+$/.test(str);
};
export const validateString = (value?: string | null, options: any = {}) => {
  const { minLength, maxLength, validateLegality = true, matchPattern, validateSecure } = options;
  if (!value)
    return {
      failed: false,
      message: 'success',
    };

  // Length validation
  if (minLength !== undefined && value.length < minLength) {
    return {
      failed: true,
      message: `must contain at least ${minLength} characters.`,
    };
  }

  if (maxLength !== undefined && value.length > maxLength) {
    return {
      failed: true,
      message: `exceeds the maximum character limit (${maxLength})`,
    };
  }

  // Legality validation
  // Character legality validation
  if (validateLegality && !isLegalString(value)) {
    return {
      failed: true,
      message:
        "This field must contain at least one language character, letter, number, underscore, whitespace, or special character such as | ( ) [ ] - / . { } ' ” # $ % ^ & ! ; ? = @ : * , + ~.",
    };
  }

  // SQL injection risk validation
  if (validateLegality && !isSecureInput(value)) {
    return {
      failed: true,
      message: VALIDATE_SECURE_INPUT_MESSAGE,
    };
  }
  if (validateSecure && !isSecureInput(value)) {
    return {
      failed: true,
      message: VALIDATE_SECURE_INPUT_MESSAGE,
    };
  }

  if (matchPattern && isOnlyStars(value)) {
    return {
      failed: true,
      message: MATCH_PATTERN_ERROR_MESSAGE,
    };
  }

  return {
    failed: false,
    message: 'success',
  };
};
export const validateMappedEventData = (
  mappedEvent: CriticalEvent,
  needValidateEventName: boolean = true,
): ValidateResult => {
  //validate mapped eventname and event_category
  if (!mappedEvent.name) {
    return {
      key: ['Mapped event name'],
      result: 'is null',
      status: false,
      fields: needValidateEventName ? ['mapped_event_name'] : [],
      errorIndex: {},
    };
  }
  //check mapped events
  let eventRes: ValidateResult = {
    key: ['event name'],
    result: 'is null',
    status: false,
    fields: [],
    errorIndex: {},
  };
  if (needValidateEventName) {
    eventRes = {
      key: ['Select at least one raw event to proceed.'],
      result: '',
      status: false,
      fields: ['raw_event_name'],
      errorIndex: {},
    };
  }
  const eventStatus = mappedEvent.rules.every((andRules, andIndex) => {
    return (andRules ?? []).every((orRule, index) => {
      // Check that raw_event_name contains special characters.
      const { failed, message } = validateString(orRule?.value?.[0], {
        validateLegality: false,
        validateSecure: true,
      });
      if (failed) {
        eventRes.key = ['[raw event]:'];
        eventRes.result = message;
        eventRes.errorIndex[index] = [0];
      }
      if (!orRule?.value?.length) {
        eventRes.key = ['[raw event]:'];
        eventRes.result = 'is null';
        eventRes.fields = ['raw_event_name'];
        eventRes.errorIndex[index] = [0];
      }

      return orRule?.value?.length && orRule?.op && !failed;
    });
  });
  if (!eventStatus) return eventRes;

  // remove rules without tag_key and tag_value
  mappedEvent.rules.forEach((andRules) => {
    andRules.forEach((orRule) => {
      orRule.mapping_rules = orRule.mapping_rules.filter((andMappingRules) => {
        return andMappingRules.filter((orMappingRule) => {
          return (
            orMappingRule.tag_key.value.length ||
            orMappingRule.tag_value.value.length ||
            isEmptyOrNotEmptyOp(orMappingRule.tag_value.op)
          );
        }).length;
      });
    });
  });

  // Checks if tag_key and tag_value satisfy specified conditions.
  const eventRuleRes = {
    key: [] as string[],
    result: 'is null',
    status: false,
    fields: ['tag_key_value'],
    errorIndex: {},
  };
  const checkEventRules = (mappedEvent: any) => {
    return checkAllRules(mappedEvent.rules, (rule) => {
      const hasKey = rule.tag_key.op && rule.tag_key.value.length > 0;
      const hasValue = rule.tag_value.op && rule.tag_value.value.length > 0;
      const { failed: isIllegalityTagKey, message: validateTagKeyMessage } = validateString(
        rule.tag_key.value?.[0],
      );
      const { failed: isllegalityTagValue, message: validateTagValueMessage } = validateString(
        rule.tag_value.value?.[0],
        {
          matchPattern: rule.tag_value.op === EStringOp.MATCH_PATTERN,
          validateLegality: false,
          validateSecure: true,
        },
      );

      // Check that tag_key contains special characters.
      if ((hasKey && isIllegalityTagKey) || (hasValue && isllegalityTagValue)) {
        if (isIllegalityTagKey) {
          eventRuleRes.key = ['[tag key]:'];
        }
        if (isllegalityTagValue) {
          eventRuleRes.key = ['[tag value]:'];
        }
        if (isIllegalityTagKey && isllegalityTagValue) {
          eventRuleRes.key = ['[tag key OR tag value]:'];
        }
        if (isllegalityTagValue && rule.tag_value.op === EStringOp.MATCH_PATTERN) {
          eventRuleRes.key = ['[tag value]:'];
          eventRuleRes.fields = ['match_pattern_error'];
        }

        if (isIllegalityTagKey) {
          eventRuleRes.result = `${validateTagKeyMessage}`;
        } else {
          eventRuleRes.result = `${validateTagValueMessage}`;
        }
        return !isIllegalityTagKey && !isllegalityTagValue;
      } else if (isEmptyOrNotEmptyOp(rule.tag_value.op)) {
        if (!hasKey && eventRuleRes.key.length === 0) eventRuleRes.key = ['tag key'];
        eventRuleRes.result = 'is null';
        return hasKey;
      } else {
        if (!(hasKey && hasValue) && eventRuleRes.key.length === 0)
          eventRuleRes.key = ['tag key', 'tag value'];
        eventRuleRes.result = 'is null';
        return hasKey && hasValue;
      }
    });
  };

  const mappedEventCheckFailed = !checkEventRules(mappedEvent);
  if (mappedEventCheckFailed) {
    return eventRuleRes;
  }

  return { mappedEvent: mappedEvent, status: true, fields: [], errorIndex: {} };
};

export const isEmptyMappedEventRules = (rules: CriticalEventRule[][] = []): boolean => {
  if (!rules?.length) return true;

  const eventRules = trimMappedEventRules(rules) ?? [];
  if (eventRules.length === 0) return true;

  // Determine whether there is only one empty rules, and this rule is equal to the getEmptyRuleGroupInAPIForm ()
  return eventRules.length === 1 && isEqual(eventRules[0], [getEmptyRuleGroupInAPIForm()]);
};
export const validateMappedEventName = (mappedEventName: string) => {
  // Mapped event name need to less than 50
  if (mappedEventName.length > 50) {
    return {
      failed: true,
      message: 'Exceeds the maximum character limit (50)',
    };
  }

  // Validate legality

  const regex = /^[\u4e00-\u9fa5\w\s|()[\]\-\/\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]+$/u;
  if (!regex.test(mappedEventName) && mappedEventName !== '') {
    return {
      failed: true,
      message:
        'Must contain only letters, numbers, spaces, emoji and special characters: _, (, ), [, ], /, -, |.',
    };
  }

  // SQL injection risk validation
  if (!isSecureInput(mappedEventName)) {
    return {
      failed: true,
      message: VALIDATE_SECURE_INPUT_MESSAGE,
    };
  }

  // Validate prefix
  const INVALID_PREFIX = 'conviva_';
  if (mappedEventName.toLowerCase().startsWith(INVALID_PREFIX)) {
    return {
      failed: true,
      message: `Cannot start with '${INVALID_PREFIX}'`,
    };
  }

  return { failed: false, message: 'passed' };
};
